/**
 * Helpers for primary Dx validation analytics attributes.
 * @module diagnosisValidatedAnalyticsHelpers
 */
import {
  AuthOnFile,
  PrimaryDiagnosisDetails,
} from "../../../../../data-model/medicalPaOrdersModel";
import { getAllJCodesFromAuthOnFile } from "../../../../../pages/singleOrderLayout/utils/existingDocsToLs";
import { TableData } from "../../types";

/** Normalized provenance for a Dx code relative to chart/stack config. */
export type DxSourceKind = "nccn" | "cms" | "custom" | "none";

/**
 * Maps raw source labels (NCCN/CMS/Custom) to the analytics enum.
 * Unrecognized non-empty labels fall through to `custom`.
 *
 * @param raw - Source string, comma-joined string, or source array.
 * @returns Normalized source kind.
 */
export const normalizeDxSource = (
  raw?: string | string[] | null,
): DxSourceKind => {
  if (!raw) {
    return "none";
  }
  const values = (Array.isArray(raw) ? raw : raw.split(","))
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);
  if (values.length === 0) {
    return "none";
  }
  if (values.some((value) => value.includes("nccn"))) {
    return "nccn";
  }
  if (values.some((value) => value.includes("cms"))) {
    return "cms";
  }
  return "custom";
};

/**
 * Resolves Dx source for a code from stack details, then chart table rows.
 *
 * @param icdCode - ICD code to look up.
 * @param primaryDiagnosisDetails - Stack-emitted primary diagnosis entries.
 * @param tableData - On-chart patient Dx rows.
 * @returns Normalized source for the code, or `none` when the code is empty
 *   or not found in stack/chart data.
 */
export const resolveDxSourceForCode = (
  icdCode: string,
  primaryDiagnosisDetails?: PrimaryDiagnosisDetails[],
  tableData?: TableData[],
): DxSourceKind => {
  const code = icdCode.trim();
  if (!code) {
    return "none";
  }

  const fromStack = primaryDiagnosisDetails?.find(
    (entry) => (entry.icd_code ?? "").trim() === code,
  );
  if (fromStack?.source?.length) {
    return normalizeDxSource(fromStack.source);
  }

  const fromChart = tableData?.find(
    (row) => (row.icd10_code ?? "").trim() === code,
  );
  if (fromChart?.source) {
    return normalizeDxSource(fromChart.source);
  }

  return "none";
};

/**
 * Extracts distinct J-codes and drug names from auth-on-file.
 *
 * @param authOnFile - Order auth-on-file payload.
 * @returns Parallel lists of J-codes and drug display names.
 */
export const extractJCodesAndDrugNames = (
  authOnFile?: AuthOnFile,
): { jCodes: string[]; drugNames: string[] } => {
  const details = getAllJCodesFromAuthOnFile(authOnFile) ?? [];
  const jCodes = [
    ...new Set(
      details
        .map((entry) => entry.j_code?.trim() ?? "")
        .filter((code) => !!code),
    ),
  ];
  const drugNames = [
    ...new Set(
      details
        .map((entry) => entry.medicine_name?.trim() ?? "")
        .filter((name) => !!name),
    ),
  ];
  return { jCodes, drugNames };
};
